import { useLocale } from 'next-intl';
import Image from '@/components/image';
import { useState, useRef } from 'react';
import { validatePrice } from '@/utils/category';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowIcon from '@/components/ui/icons/arrow-icon';
import ZoomInIcon from '@/components/ui/icons/zoom-in-icon';
import ShareButton from '@/components/ui/buttons/share-button';
import AddToWishlist from '@/components/ui/buttons/add-to-wishlist';
import FlashSaleCountdown from '@/components/ui/badges/flash-sale-countdown';
import { Navigation, Thumbs, Keyboard, A11y, Pagination } from 'swiper/modules';
// import ProductLabel from '@/components/ui/cards/product-card-label';

const MediaMainSwiper = ({
  thumbsSwiper,
  currentImg,
  mainSwiperRef,
  setCurrentImg,
  prevRef,
  nextRef,
  images,
  title,
  hasImages,
  isRTL,
  sku,
  product,
  description,
  // setOpenFancybox,
  t,
  priceWithTax,
}) => {
  const price = validatePrice(priceWithTax);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const imageContainerRef = useRef(null);
  const locale = useLocale();
  const handleMouseMove = (e) => {
    if (!imageContainerRef.current) return;

    const container = imageContainerRef.current;
    const { left, top, width, height } = container.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setZoomPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    });
  };

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  const handleTouchStart = (e) => {
    const touchStartX = e.touches[0].clientX;
    const touchThreshold = 50;

    const handleTouchMove = (e) => {
      const touchCurrentX = e.touches[0].clientX;
      const touchDiff = touchStartX - touchCurrentX;

      if (Math.abs(touchDiff) > touchThreshold) {
        if (touchDiff > 0) {
          // Next image
          if (mainSwiperRef.current) {
            mainSwiperRef.current.slideNext();
          }
        } else {
          // Previous image
          if (mainSwiperRef.current) {
            mainSwiperRef.current.slidePrev();
          }
        }
        document.removeEventListener('touchmove', handleTouchMove);
      }
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener(
      'touchend',
      () => {
        document.removeEventListener('touchmove', handleTouchMove);
      },
      { once: true }
    );
  };

  return (
    <div className='border-border relative flex items-center justify-center overflow-hidden rounded-xl bg-white sm:border'>
      <Swiper
        modules={[Navigation, Thumbs, Keyboard, A11y, Pagination]}
        thumbs={{
          swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
        }}
        key={locale}
        dir={locale === 'en' ? 'ltr' : 'rtl'}
        initialSlide={currentImg}
        speed={400}
        keyboard={{
          enabled: true,
          onlyInViewport: true,
        }}
        a11y={{
          enabled: true,
          prevSlideMessage: 'Previous image',
          nextSlideMessage: 'Next image',
        }}
        allowTouchMove
        onSwiper={(swiper) => {
          mainSwiperRef.current = swiper;
          if (swiper.params.navigation) {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
          }
        }}
        onSlideChange={(swiper) => {
          setCurrentImg(swiper.activeIndex);
        }}
        pagination={{ enabled: false }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
          disabledClass: 'opacity-30 cursor-not-allowed',
        }}
        breakpoints={{
          320: { pagination: { clickable: true, enabled: true } },
          1024: { pagination: { clickable: false, enabled: false } },
        }}
        className='pdp-swiper size-full'
      >
        {images.map((img, index) => (
          <SwiperSlide key={index} virtualIndex={index}>
            <div
              ref={imageContainerRef}
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              className='relative aspect-square w-full overflow-hidden'
            >
              <Image
                src={img?.image}
                alt={img?.alt || `${title} - Image ${index + 1}`}
                className={`max-h-full min-h-full object-contain p-5 transition-transform duration-200 ${
                  isZoomed ? 'lg:scale-125' : ''
                }`}
                priority={index === 0}
                fill={true}
                loading={index === 0 ? 'eager' : 'lazy'}
                style={{
                  transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                }}
              />

              <div className='absolute bottom-20 left-0 z-50 hidden w-full text-center lg:block'>
                <span className='rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-gray-700'>
                  {t('Hover to zoom')}
                </span>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Navigation arrows */}
      {hasImages && images.length > 1 && (
        <>
          <button
            ref={isRTL ? nextRef : prevRef}
            type='button'
            aria-label={isRTL ? 'Next image' : 'Previous image'}
            className='absolute top-1/2 left-0 z-1 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition-all hover:bg-white hover:shadow-lg max-sm:size-7 sm:left-2'
            disabled={
              locale === 'en'
                ? currentImg === 0
                : currentImg === images.length - 1
            }
          >
            <ArrowIcon
              className={`mx-auto block size-2.5 rotate-90 sm:size-4 ${(locale === 'en' ? currentImg === 0 : currentImg === images.length - 1) ? 'text-gray-400' : 'text-gray-600'}`}
            />
          </button>

          <button
            ref={isRTL ? prevRef : nextRef}
            type='button'
            aria-label={isRTL ? 'Previous image' : 'Next image'}
            className='absolute top-1/2 right-0 z-1 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition-all hover:bg-white hover:shadow-lg max-sm:size-7 sm:right-2'
            disabled={
              locale === 'en'
                ? currentImg === images.length - 1
                : currentImg === 0
            }
          >
            <ArrowIcon
              className={`mx-auto block size-2.5 rotate-270 sm:size-4 ${(locale === 'en' ? currentImg === images.length - 1 : currentImg === 0) ? 'text-gray-400' : 'text-gray-600'}`}
            />
          </button>
        </>
      )}

      {/* <div className='absolute bottom-4 left-4 z-1 flex items-center gap-2 max-sm:flex-col'>
        {labels
          ?.filter((el) => el && el?.is_active === '1')
          ?.map((label, i) => (
            <ProductLabel label={label} key={i} />
          ))}
      </div> */}

      {/* Action buttons */}
      <div className='absolute top-4 right-4 z-1 flex items-center gap-2 max-sm:flex-col'>
        <AddToWishlist fromPdp sku={sku} product={product} />
        <ShareButton title={title} description={description} />
        <button
          type='button'
          // onClick={() => setOpenFancybox(true)}
          onClick={() => setIsZoomed(!isZoomed)}
          aria-label='Zoom in on image'
          title='Zoom in on image'
          className='flex size-10 items-center justify-center rounded-full bg-white/90 p-1 shadow-md transition-all hover:bg-white hover:shadow-lg max-sm:hidden lg:p-2'
        >
          <ZoomInIcon className='size-6 text-gray-600 max-[768px]:size-4' />
        </button>
      </div>

      <FlashSaleCountdown
        startDate={product?.flash_sale_start_date}
        endDate={product?.flash_sale_end_date}
        className='absolute start-3 top-3 z-50 max-sm:start-2 max-sm:top-2'
      />

      {/* Discount badge */}
      {price?.discountPercentage && (
        <div className='absolute top-3 left-3 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-[#ef4444] text-xs font-bold text-white shadow-md lg:top-4 lg:left-4 lg:h-12 lg:w-12'>
          -{price?.discountPercentage}%
        </div>
      )}

    </div>
  );
};

export default MediaMainSwiper;
